import { PlaybackInfoPlugin } from '@svt/videoplayer-plugin-playbackinfo';
import { VideoPlayer } from '@svt/videoplayer-react';
import theme from '@svt/videoplayer-styling/dist/css/default';
import '@svt/videoplayer-styling/dist/css/default.css';
import { VisuallyHiddenStyle } from 'app/ui-components/utils.styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import svtDataSdkClient from '../../../../client/dataLake';
import env from '../../../env';
import BackButton from './BackButton';
import ContinueWatchingContainer from './Plugins/ContinueWatchingContainer.jsx';
import CurrentPosition from './Plugins/CurrentPosition.jsx';
import EndScreenContainer from './Plugins/EndScreen/EndScreenContainer.jsx';
import ParentalControlContainer from './Plugins/ParentalControl/ParentalControlContainer.jsx';
import ProgramInfo from './Plugins/ProgramInfo';
import ProgressTracking from './Plugins/ProgressTracking';
import { VideoPlayerContainer } from './VideoPlayer.styles';

const AutoPlayBlockDetector = ({ state, callback }) => {
    const { autoPlayWasBlocked } = state;

    useEffect(() => {
        callback(autoPlayWasBlocked);
    }, [autoPlayWasBlocked, callback]);

    return autoPlayWasBlocked ? (
        <VisuallyHiddenStyle aria-live="assertive" as="div">
            Programmet kunde inte starta automatiskt. Tryck på "spela" igen för att starta programmet.
        </VisuallyHiddenStyle>
    ) : null;
};

const Plugins = ({
    state,
    barn,
    videoId,
    enableContinueWatching,
    __typename,
    __videoType,
    visionId,
    titleArticleId,
    escenicId,
    setEndScreenVisible,
    isKidsGenre,
    videoLength,
    showParentalLock,
    setAutoPlayWasBlocked,
    hasError,
    isBrowserFullscreen,
    isSimulcast,
}) => {
    return (
        <>
            <AutoPlayBlockDetector state={state} callback={setAutoPlayWasBlocked} />
            {isBrowserFullscreen && <CurrentPosition videoId={videoId} position={state.position} />}
            {enableContinueWatching && (
                <ContinueWatchingContainer
                    barn={barn}
                    videoPlayer={state.controller}
                    position={state.position}
                    duration={state.duration}
                    streamInfo={state.streamInfo}
                    __typename={__typename}
                    visionId={visionId}
                    titleArticleId={titleArticleId}
                />
            )}

            {showParentalLock && (
                <ParentalControlContainer
                    index={1}
                    active={true}
                    videoPlayer={state.controller}
                    playerState={state.playerState}
                    videoId={videoId}
                />
            )}
            {!isSimulcast && (
                <EndScreenContainer
                    key={videoId}
                    escenicId={escenicId}
                    visionId={visionId}
                    currentVideoType={__videoType}
                    castState={state.castState}
                    videoPlayer={state.controller}
                    playerState={state.playerState}
                    isKidsGenre={isKidsGenre}
                    onDisplayEndScreen={setEndScreenVisible}
                />
            )}

            {(hasError ||
                state.isGeoBlocked ||
                state.playerState === 'AWAITING_LIVE' ||
                (state.autoPlayWasBlocked && state.playerState === 'LOADED')) &&
                isBrowserFullscreen && <BackButton>Tillbaka</BackButton>}
            <ProgressTracking videoLength={videoLength} position={state.position} />
        </>
    );
};

const UIPlugins = ({ isBrowserFullscreen, state, title, episodeTitle }) => (
    <>
        <PlaybackInfoPlugin {...state} />
        <ProgramInfo title={title} episodeTitle={episodeTitle} />
        {isBrowserFullscreen && <BackButton>Tillbaka</BackButton>}
    </>
);

const VideoPlayerNew = ({
    videoId,
    startPosition,
    positionFromQuery,
    autoPlay,
    poster,
    limitBandWidth,
    className,
    bingeAutoPlay,
    isBrowserFullscreen,
    isChildProtected,
    parentalLockConfirmed,
    highlights,
    barn,
    enableContinueWatching,
    __typename,
    __videoType,
    visionId,
    titleArticleId,
    escenicId,
    isKidsGenre,
    videoLength,
    autoPlayEnabled,
    parentalLockEnabled,
    continueWatchingEnabled,
    title,
    episodeTitle,
    isSimulcast,
    splash = false,
}) => {
    const [hasError, setHasError] = useState(false);
    const [endScreenVisible, setEndScreenVisible] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [autoPlayWasBlocked, setAutoPlayWasBlocked] = useState(false);

    const parentalLockIsVisible = isChildProtected && !parentalLockConfirmed && !hasError;

    const comscoreProps = {
        clientName: 'svt-play',
        production: env.COMSCORE_ENV === 'prod',
        autoPlayOfNextVideo: bingeAutoPlay,
    };

    const helixProps = {
        sdk: svtDataSdkClient,
        customValues: {
            autoplayNext: autoPlayEnabled ? 'on' : 'off',
            continueWatching: continueWatchingEnabled ? 'on' : 'off',
            enhancedParentalLock: parentalLockEnabled ? 'on' : 'off',
            playerLimitBandwidth: limitBandWidth ? 'on' : 'off',
        },
    };

    return (
        <VideoPlayerContainer
            data-rt="video-player-fullscreen"
            theme={theme}
            isKeepWatching={startPosition > 0}
            isHidden={parentalLockIsVisible || endScreenVisible}
            isBrowserFullscreen={isBrowserFullscreen}
            className={className}
        >
            <VideoPlayer
                videoId={videoId}
                poster={poster}
                autoPlay={autoPlay && !parentalLockIsVisible}
                startPosition={startPosition > 0 ? startPosition : undefined}
                aspectRatio="16:9"
                disableStatistics={env.FLAG_ENABLE_DATA_LAKE !== true}
                theme={theme}
                disableUi={parentalLockIsVisible}
                // splash={autoPlayWasBlocked || splash} // Disabled due to bug with chrome cast
                splash
                onErrorCallback={() => setHasError(true)}
                enableVideoSettings={true}
                disableShortcuts={endScreenVisible}
                highlights={highlights}
                additionalSettingsUrl="//www.svtplay.se/installningar"
                comscoreProps={comscoreProps}
                helixProps={helixProps}
                plugins={(state) => (
                    <Plugins
                        state={state}
                        isBrowserFullscreen={isBrowserFullscreen}
                        barn={barn}
                        videoId={videoId}
                        visionId={visionId}
                        positionFromQuery={positionFromQuery}
                        enableContinueWatching={enableContinueWatching}
                        autoPlay={autoPlay}
                        __typename={__typename}
                        __videoType={__videoType}
                        titleArticleId={titleArticleId}
                        escenicId={escenicId}
                        setEndScreenVisible={setEndScreenVisible}
                        isKidsGenre={isKidsGenre}
                        videoLength={videoLength}
                        showParentalLock={parentalLockIsVisible}
                        setAutoPlayWasBlocked={setAutoPlayWasBlocked}
                        hasError={hasError}
                        isSimulcast={isSimulcast}
                    />
                )}
                uiPlugins={(state) => (
                    <UIPlugins
                        isBrowserFullscreen={isBrowserFullscreen}
                        state={state}
                        title={title}
                        episodeTitle={episodeTitle}
                    />
                )}
            />
        </VideoPlayerContainer>
    );
};

VideoPlayerNew.propTypes = {
    videoId: PropTypes.string.isRequired,
    poster: PropTypes.string,
    className: PropTypes.string,
    autoPlay: PropTypes.bool,
    startPosition: PropTypes.number,
    positionFromQuery: PropTypes.number,
    enableContinueWatching: PropTypes.bool,
    bingeAutoPlay: PropTypes.bool,
    setCurrentVideoPosition: PropTypes.func,
    isKidsGenre: PropTypes.bool,
    highlights: PropTypes.array,
    autoPlayEnabled: PropTypes.bool,
    parentalLockEnabled: PropTypes.string,
    continueWatchingEnabled: PropTypes.bool,
    title: PropTypes.string,
    episodeTitle: PropTypes.string,
    isSimulcast: PropTypes.bool,
};

VideoPlayerNew.defaultProps = {
    reducedBandwidth: false,
    autoPlay: false,
    autoFocusOnPlay: false,
    enableContinueWatching: true,
    setCurrentVideoPosition: null,
    isBrowserFullscreen: true,
    isSimulcast: false,
};

export default VideoPlayerNew;
